// src/components/doctor/OrdersStep.jsx
//
// Step 3 — what happens after the visit: lab tests (a search box, a handful of
// recommended chips and a "Browse all" list — not a wall of chips), other
// instructions, and the follow-up. Everything here reaches the patient as a
// care-plan next step once the visit is signed. Presentational: next steps live
// in the visit form (useConsultation); lab tests are folded into ONE lab-suggestion
// step by utils/labOrders.js.

import React, { useMemo, useRef, useState } from 'react';
import useLabRecommendations, { REASON_LABEL } from '../../hooks/useLabRecommendations';
import { VisibleToPatient } from './ConsultTags';
import { followUpSentence } from '../../utils/consultSteps';
import { NEXT_STEP_PRIORITIES, NEXT_STEP_MAX_CHARS, FOLLOW_UP_WEEKS } from '../../utils/consultation';
import {
  labOrderFromSteps, instructionSteps, addLabTest, removeLabTest, setLabWeeks, filterTestOptions, topRecommended,
} from '../../utils/labOrders';

const PRIORITY_LABEL = { MEDIUM: 'Medium', LOW: 'Low', ROUTINE: 'Routine' };
const fmtDue = iso => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/* ── Lab test search combobox ─────────────────────────────────────────────── */
function TestSearch({ options, selected, disabled, onPick }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const results = useMemo(() => filterTestOptions(options, q, selected), [options, q, selected]);
  const typed = q.trim();
  const canAddTyped = typed && !results.some(r => r.name.toLowerCase() === typed.toLowerCase())
    && !selected.some(s => s.toLowerCase() === typed.toLowerCase());
  const rows = canAddTyped ? [...results, { name: typed, custom: true }] : results;

  const pick = o => { onPick(o.name); setQ(''); setOpen(false); setActive(0); };
  const onKeyDown = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && rows[active]) pick(rows[active]); else if (typed) pick({ name: typed }); }
    else if (e.key === 'Escape') { if (open) { e.stopPropagation(); setOpen(false); } }
  };
  const showList = open && rows.length > 0;
  return (
    <div className="consult-combo">
      <label className="sr-only" htmlFor="lab-test-search">Search lab tests</label>
      <input id="lab-test-search" className="form-input" type="text" autoComplete="off" role="combobox" aria-expanded={showList}
        aria-controls="lab-test-list" aria-autocomplete="list" aria-activedescendant={showList ? `lab-test-opt-${active}` : undefined}
        disabled={disabled} value={q} placeholder={`Search ${options.length} tests — e.g. HbA1c, lipid, thyroid`}
        onChange={e => { setQ(e.target.value); setOpen(true); setActive(0); }} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKeyDown} />
      {showList && (
        <ul id="lab-test-list" role="listbox" className="consult-combo__list" aria-label="Matching lab tests">
          {rows.map((o, i) => (
            <li key={o.name} id={`lab-test-opt-${i}`} role="option" aria-selected={i === active} className="consult-combo__opt"
              onMouseDown={e => { e.preventDefault(); pick(o); }} onMouseEnter={() => setActive(i)}>
              <span>{o.custom ? `Add “${o.name}” as typed` : o.name}</span>
              {!o.custom && REASON_LABEL[o.reason] && <span className="card-row-meta">{REASON_LABEL[o.reason]}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Follow-up: a radiogroup (None / 2 / 4 / 8 / 12 weeks) ─────────────────── */
const FOLLOW_UP_OPTIONS = [{ v: '', t: 'None' }, ...FOLLOW_UP_WEEKS.map(w => ({ v: String(w), t: `${w} wks` }))];

// Arrow keys move the selection (WAI-ARIA radio group); only the checked radio is in the Tab order.
function FollowUpPicker({ value, disabled, onChange }) {
  const refs = useRef([]);
  const checkedIndex = Math.max(0, FOLLOW_UP_OPTIONS.findIndex(o => o.v === String(value ?? '')));
  const onKeyDown = e => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step || disabled) return;
    e.preventDefault();
    const n = (checkedIndex + step + FOLLOW_UP_OPTIONS.length) % FOLLOW_UP_OPTIONS.length;
    onChange(FOLLOW_UP_OPTIONS[n].v);
    refs.current[n]?.focus();
  };
  return (
    <div className="consult-seg" role="radiogroup" aria-labelledby="followup-label" aria-describedby="followup-explain" onKeyDown={onKeyDown}>
      {FOLLOW_UP_OPTIONS.map((o, i) => (
        <button key={o.t} type="button" role="radio" className="consult-seg__btn" disabled={disabled} ref={el => { refs.current[i] = el; }}
          aria-checked={i === checkedIndex} tabIndex={i === checkedIndex ? 0 : -1} onClick={() => onChange(o.v)}>{o.t}</button>
      ))}
    </div>
  );
}

export default function OrdersStep({
  patientId, form, setField, setNextSteps, locked, onBack, onNext,
}) {
  const labs = useLabRecommendations(patientId);
  const [browsing, setBrowsing] = useState(false);
  const order = labOrderFromSteps(form.nextSteps);
  const tests = order.markers;
  const instructions = instructionSteps(form.nextSteps);
  const chips = topRecommended(labs.recommended, tests);
  const remaining = filterTestOptions(labs.options, '', tests, 200);

  const patchStep = (index, patch) => setNextSteps(list => list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const addTest = name => setNextSteps(list => addLabTest(list, name));

  return (
    <section className="card consult-work" aria-labelledby="consult-step-title">
      <h2 className="consult-work__title" id="consult-step-title" tabIndex={-1}>Orders &amp; follow-up</h2>
      <div className="consult-work__why">What happens after the visit. Everything here is shown to the patient as their next steps.</div>

      <div className="consult-field">
        <div className="consult-field__head"><span className="form-label" id="lab-tests-label">Lab tests</span><VisibleToPatient /></div>
        {!locked && <TestSearch options={labs.options} selected={tests} disabled={locked} onPick={addTest} />}
        {!locked && (
          <div className="consult-chips" style={{ margin: '10px 0', alignItems: 'center', gap: 8 }} role="group" aria-label="Recommended from the latest report">
            {labs.status === 'loading' && <span className="card-row-meta">Loading recommendations…</span>}
            {labs.status === 'error' && <span className="card-row-meta">Recommendations are unavailable right now — you can still search.</span>}
            {labs.status === 'ready' && chips.length === 0 && <span className="card-row-meta">Nothing is due for retest.</span>}
            {chips.length > 0 && <span className="card-row-meta">From the latest report:</span>}
            {chips.map(o => (
              <button key={o.name} type="button" className="consult-chip consult-chip--add" onClick={() => addTest(o.name)}
                aria-label={`Add ${o.name}${REASON_LABEL[o.reason] ? `, ${REASON_LABEL[o.reason]}` : ''}`}>
                + {o.name}{REASON_LABEL[o.reason] ? ` · ${REASON_LABEL[o.reason]}` : ''}
              </button>
            ))}
            <button type="button" className="consult-link" aria-expanded={browsing} aria-controls="lab-browse"
              onClick={() => setBrowsing(b => !b)}>{browsing ? 'Hide list' : `Browse all ${labs.options.length} →`}</button>
          </div>
        )}
        {browsing && !locked && (
          <div id="lab-browse" className="consult-browse" role="group" aria-label="All lab tests">
            {remaining.length === 0 ? <span className="card-row-meta">Everything on the list has been added.</span> : (
              <div className="consult-chips" style={{ gap: 8 }}>
                {remaining.map(o => (
                  <button key={o.name} type="button" className="consult-chip consult-chip--add" onClick={() => addTest(o.name)}>
                    + {o.name}{REASON_LABEL[o.reason] ? ` · ${REASON_LABEL[o.reason]}` : ''}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }} aria-labelledby="lab-tests-label">
          {tests.map(t => (
            <li key={t} className="consult-row">
              <span className="consult-row__main">{t}</span>
              <span className="consult-row__meta">due {fmtDue(order.dueDate) || `in ${order.weeks} weeks`}</span>
              {!locked && <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove ${t}`}
                onClick={() => setNextSteps(list => removeLabTest(list, t))}>✕</button>}
            </li>
          ))}
        </ul>
        {tests.length === 0 && <div className="card-row-meta">No lab tests added.</div>}
        {tests.length > 0 && (
          <div className="consult-row__fields" style={{ marginTop: 6 }}>
            <label className="form-label" htmlFor="lab-weeks">Repeat in</label>
            <select id="lab-weeks" className="form-select" style={{ width: 'auto' }} disabled={locked} value={order.weeks}
              onChange={e => setNextSteps(list => setLabWeeks(list, Number(e.target.value)))}>
              {[...new Set([...FOLLOW_UP_WEEKS, order.weeks])].sort((a, b) => a - b).map(w => <option key={w} value={w}>{w} weeks</option>)}
            </select>
            <span className="form-hint">The patient gets a one-tap link to order these tests.</span>
          </div>
        )}
      </div>

      <div className="consult-field">
        <div className="consult-field__head"><span className="form-label" id="instructions-label">Other instructions</span><VisibleToPatient /></div>
        {instructions.length === 0 && <div className="card-row-meta" style={{ marginBottom: 8 }}>No other instructions yet.</div>}
        {instructions.map(({ step: s, index }, n) => (
          <div key={index} className="consult-row">
            <div className="consult-row__main">
              <input className="form-input" style={{ width: '100%' }} disabled={locked} maxLength={NEXT_STEP_MAX_CHARS}
                aria-label={`Instruction ${n + 1}`} value={s.description} placeholder="What should the patient do?"
                onChange={e => patchStep(index, { description: e.target.value })} />
            </div>
            <div className="consult-row__fields">
              <select className="form-select" style={{ width: 'auto' }} disabled={locked} aria-label={`Priority for instruction ${n + 1}`}
                value={s.priority} onChange={e => patchStep(index, { priority: e.target.value })}>
                {NEXT_STEP_PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
              </select>
              <input className="form-input" style={{ width: 'auto' }} type="date" disabled={locked} aria-label={`Due date for instruction ${n + 1}`}
                value={s.dueDate} onChange={e => patchStep(index, { dueDate: e.target.value })} />
              {!locked && <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove instruction ${n + 1}`}
                onClick={() => setNextSteps(list => list.filter((_, i) => i !== index))}>✕</button>}
            </div>
          </div>
        ))}
        {!locked && (
          <button type="button" className="btn btn-ghost btn-sm"
            onClick={() => setNextSteps(list => [...list, { description: '', priority: 'MEDIUM', dueDate: '' }])}>+ Add instruction</button>
        )}
      </div>

      <div className="consult-field">
        <div className="consult-field__head"><span className="form-label" id="followup-label">Follow-up visit</span><VisibleToPatient /></div>
        <FollowUpPicker value={form.followUpWeeks} disabled={locked} onChange={v => setField('followUpWeeks', v)} />
        <div className="form-hint" style={{ marginTop: 6 }} id="followup-explain" aria-live="polite">{followUpSentence(form.followUpWeeks)}</div>
      </div>

      <div className="consult-foot">
        <button type="button" className="btn btn-ghost" onClick={onBack}>← Back</button>
        <button type="button" className="btn btn-primary" onClick={onNext}>Continue: Review &amp; sign →</button>
      </div>
    </section>
  );
}
